import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useDispatch } from 'react-redux'
import Swal from 'sweetalert2'
import { addFaqs } from '../../slice/faqSlice'

// Validation Schema
const faqSchema = yup.object().shape({
    category: yup
        .string()
        .required('Category is required')
        .min(2, 'Category must be at least 2 characters')
        .max(100, 'Category cannot exceed 100 characters'),

    question: yup
        .string()
        .required('Question (English) is required')
        .min(5, 'Minimum 5 characters')
        .max(500, 'Maximum 500 characters'),

    answer: yup
        .string()
        .required('Answer (English) is required')
        .min(5, 'Minimum 5 characters'),

    question_ar: yup
        .string()
        .required('Question (Arabic) is required')
        .max(500, 'Maximum 500 characters'),

    answer_ar: yup
        .string('Answer (Arabic) is required')
        .required(),

    is_active: yup.boolean()
})

const AddFaq = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset
    } = useForm({
        resolver: yupResolver(faqSchema),
        defaultValues: {
            category: '',
            question: '',
            answer: '',
            question_ar: '',
            answer_ar: '',
            is_active: true
        }
    })

    const onSubmit = async (data) => {
        setIsSubmitting(true)

        try {
            const payload = {
                category: data.category,
                question: data.question,
                answer: data.answer,
                question_ar: data.question_ar || null,
                answer_ar: data.answer_ar || null,
                is_active: data.is_active ? 1 : 0
            }

            const response = await dispatch(addFaqs(payload))

            if (response?.payload?.code === 200) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: response?.payload?.message || 'FAQ created successfully',
                    timer: 2000,
                    showConfirmButton: false
                })

                reset()
                setTimeout(() => navigate('/faq'), 2000)
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response?.payload?.message || 'Failed to create FAQ'
                })
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.message || 'Something went wrong'
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="container-fluid px-4 py-3">

            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0">Add FAQ</h4>
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/faq')}
                >
                    ← Back to List
                </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="card border-0 shadow-sm mb-4">
                    <div className="card-header bg-light fw-semibold">
                        FAQ Details
                    </div>

                    <div className="card-body p-4">
                        <div className="row g-3">

                            {/* Category */}
                            <div className="col-12">
                                <label className="form-label">
                                    Category <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.category ? 'is-invalid' : ''}`}
                                    {...register('category')}
                                />
                                {errors.category && (
                                    <div className="invalid-feedback">{errors.category.message}</div>
                                )}
                            </div>

                            {/* Question EN */}
                            <div className="col-12">
                                <label className="form-label">
                                    Question (English) <span className="text-danger">*</span>
                                </label>
                                <textarea
                                    className={`form-control ${errors.question ? 'is-invalid' : ''}`}
                                    rows="2"
                                    {...register('question')}
                                />
                                {errors.question && (
                                    <div className="invalid-feedback">{errors.question.message}</div>
                                )}
                            </div>

                            {/* Answer EN */}
                            <div className="col-12">
                                <label className="form-label">
                                    Answer (English) <span className="text-danger">*</span>
                                </label>
                                <textarea
                                    className={`form-control ${errors.answer ? 'is-invalid' : ''}`}
                                    rows="3"
                                    {...register('answer')}
                                />
                                {errors.answer && (
                                    <div className="invalid-feedback">{errors.answer.message}</div>
                                )}
                            </div>

                            {/* Question AR */}
                            <div className="col-12">
                                <label className="form-label">
                                    Question (Arabic) <span className="text-danger">*</span>
                                </label>
                                <textarea
                                    className="form-control"
                                    rows="2"
                                    dir="rtl"
                                    {...register('question_ar')}
                                />
                            </div>

                            {/* Answer AR */}
                            <div className="col-12">
                                <label className="form-label">
                                    Answer (Arabic) <span className="text-danger">*</span>
                                </label>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    dir="rtl"
                                    {...register('answer_ar')}
                                />
                            </div>

                            {/* Status */}
                            <div className="col-12">
                                <div className="form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        defaultChecked
                                        {...register('is_active')}
                                    />
                                    <label className="form-check-label">
                                        Active
                                    </label>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="d-flex gap-3 justify-content-end">
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => navigate('/faq')}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>

                    <button
                        type="submit"
                        className="btn btn-success"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Saving...' : 'Create FAQ'}
                    </button>
                </div>
            </form>
        </div>
    )
}

export default AddFaq